import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Button,
  DialogActions,
  CircularProgress,
  Box,
  IconButton,
  Typography,
  Grid,
} from "@mui/material";

import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import { updatePerformance } from "services/api";
import { useAuth } from "context/authContext";

export default function KpiDialog({ open, onClose, kpi, employeeId, onSave }) {
  const [editingTarget, setEditingTarget] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState(false);
  const [target, setTarget] = useState(null);
  const [achievement, setAchievement] = useState(null);
  const [kpiAssId, setKpiAssId] = useState(null);
  const { selectedMonth, setSelectedMonth, selectedYear, setSelectedYear } = useAuth(); //selectedYear,setSelectedYear
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();

  const handleClose = () => {
    setEditingTarget(false);
    onClose();
  };

  const handleSave = async () => {
    if (!employeeId || !kpiAssId) return;
    setLoading(true);
    try {
      await updatePerformance(
        employeeId,
        kpiAssId,
        selectedMonth,
        selectedYear,
        target,
        achievement,
        true
      );
      const updatedKpi = {
        ...kpi.kpi,
        target,
        achievement,
      };
      onSave?.(updatedKpi);
      setLoading(false);
      handleClose();
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (kpi?.kpi) {
      setTarget(kpi.kpi.target ?? null);
      setAchievement(kpi.kpi.achievement ?? null);
      setKpiAssId(kpi.kpi.kpiAssignmentId ?? null);
    }
  }, [kpi]);

  if (!kpi || !kpi.kpi) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: "#fff",
          color: "#000",
          borderRadius: 2,
          p: 2,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: "bold" }}>{t("user.kpiLinks.kpiDetails")}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2">{t("user.kpiLinks.kpi")}</Typography>
            <Typography variant="body1">{kpi.kpi.kpiName}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">{t("user.kpiLinks.target")}</Typography>
            {editingTarget ? (
              <TextField
                fullWidth
                size="small"
                value={target !== null && !isNaN(target) ? target.toString() : ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const parsed = parseFloat(value);

                  if (value === "" || isNaN(parsed)) {
                    setTarget(null);
                  } else {
                    setTarget(parsed);
                  }
                }}
                onBlur={() => setEditingTarget(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setEditingTarget(false);
                }}
                autoFocus
                sx={{
                  maxWidth: "150px",
                  height: "40px",
                  "& .MuiInputBase-root": { height: "40px" },
                }}
              />
            ) : (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="body1">
                  {target === null || isNaN(target) ? "-" : target}
                </Typography>{" "}
                <IconButton size="small" onClick={() => setEditingTarget(true)} sx={{ p: 0.5 }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">{t("user.kpiLinks.achievement")}</Typography>

            {editingAchievement ? (
              <TextField
                fullWidth
                size="small"
                value={achievement !== null && !isNaN(achievement) ? achievement.toString() : ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const parsed = parseFloat(value);

                  if (value === "" || isNaN(parsed)) {
                    setAchievement(null);
                  } else {
                    setAchievement(parsed);
                  }
                }}
                onBlur={() => setEditingAchievement(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setEditingAchievement(false);
                }}
                autoFocus
                sx={{
                  maxWidth: "150px",
                  height: "32px",
                  "& .MuiInputBase-root": { height: "32px" },
                }}
              />
            ) : (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="body1">
                  {achievement === null || isNaN(achievement) ? "-" : achievement}
                </Typography>

                <IconButton
                  size="small"
                  onClick={() => setEditingAchievement(true)}
                  sx={{ p: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">{t("user.kpiLinks.weight")}</Typography>
            <Typography variant="body1">{kpi.kpi.weight ?? "-"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">{t("user.kpiLinks.secondaryAchievement")}</Typography>
            <Typography variant="body1">{kpi.kpi.secondaryAchievement ?? "-"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">{t("user.kpiLinks.perspective")}</Typography>
            <Typography variant="body1">
              {kpi?.kpi?.perspectiveName
                ? t(`user.assess.perspectives.${kpi.kpi.perspectiveName}`, kpi.kpi.perspectiveName)
                : "-"}
            </Typography>{" "}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">{t("user.kpiLinks.formula")} </Typography>
            <Typography variant="body1">
              {kpi?.kpi?.kpiFormula
                ? kpi.kpi.kpiFormula === "Regular"
                  ? t("user.myScorecard.regular")
                  : t("user.myScorecard.reverse")
                : "-"}
            </Typography>{" "}
          </Grid>
        </Grid>
      </DialogContent>
      {!loading && (
        <DialogActions sx={{ justifyContent: "flex-end" }}>
          <Button
            onClick={handleSave}
            variant="contained"
            color="success"
            //  sx={{ mr: 1 }}
            sx={{ mr: 1, "&:hover": { backgroundColor: "blue", color: "#fff" } }}
          >
            {t("common.save")}
          </Button>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: "#fff",
              color: "#888888",
              "&:hover": {
                backgroundColor: "#fff",
              },
            }}
          >
            {t("common.close")}
          </Button>
        </DialogActions>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Dialog>
  );
}

KpiDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  kpi: PropTypes.shape({
    finalResult: PropTypes.number.isRequired,
    weightedResult: PropTypes.number.isRequired,
    kpi: PropTypes.shape({
      kpiName: PropTypes.string.isRequired,
      weight: PropTypes.number.isRequired,
      target: PropTypes.number.isRequired,
      achievement: PropTypes.number, // optional if it can be null
      kpiFormula: PropTypes.string.isRequired,
      secondaryAchievement: PropTypes.number.isRequired,
      perspectiveName: PropTypes.string.isRequired,
      primaryPercentage: PropTypes.number.isRequired,
      secondaryPercentage: PropTypes.number.isRequired,
      kpiAssignmentId: PropTypes.number.isRequired,
    }).isRequired,
  }),
  employeeId: PropTypes.number.isRequired,
};
